/**
 * VCM Data Layer Implementation — shared helpers for GA4 ecommerce dataLayer.push() events.
 * See docs/gtm-tag-trigger-variable-reference.md for the full event/tag map this feeds.
 */
window.wkDataLayer = (function () {
  function push(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  // productId/variantId are Shopify numeric IDs; matches the doc's item_id convention.
  function buildItem({
    productId,
    variantId,
    productName,
    productType,
    genderCategory,
    variantTitle,
    price,
    quantity,
    itemListName,
    personalizationType,
    personalizationValue,
  }) {
    const item = {
      item_id: `shopify_US_${productId}_${variantId}`,
      item_name: productName,
      item_brand: 'W.Kleinberg',
      item_category: productType || '',
      item_variant: variantTitle || '',
      price: Number(price) || 0,
      quantity: quantity || 1,
    };
    if (genderCategory) item.item_category_2 = genderCategory;
    if (itemListName) item.item_list_name = itemListName;
    if (personalizationType) {
      item.personalization_type = personalizationType;
      item.personalization_value = Number(personalizationValue) || 0;
    }
    return item;
  }

  function pushEcommerceEvent(eventName, { currency, value, coupon, items, personalization }) {
    const payload = {
      event: eventName,
      ecommerce: {
        currency: currency || 'USD',
        value: Number(value) || 0,
        items: items || [],
      },
    };
    if (coupon !== undefined) payload.ecommerce.coupon = coupon;
    if (personalization) {
      payload.personalization = {
        type: personalization.type || '',
        value: Number(personalization.value) || 0,
      };
    }
    push(payload);
  }

  // select_item: delegated so every product-block (PLP, New Arrivals, Most Popular,
  // related products, search results, etc.) is covered without touching each section.
  document.addEventListener('click', function (evt) {
    const link = evt.target.closest('.product-link');
    if (!link) return;
    const block = link.closest('product-block');
    if (!block) return;

    pushEcommerceEvent('select_item', {
      value: Number(block.dataset.productPrice) || 0,
      items: [
        buildItem({
          productId: block.dataset.productId,
          variantId: block.dataset.variantId,
          productName: block.dataset.productName,
          productType: block.dataset.productType,
          variantTitle: '',
          price: block.dataset.productPrice,
          quantity: 1,
          itemListName: block.dataset.itemListName || undefined,
        }),
      ],
    });
  });

  // select_item: PDP buckle/strap cross-sell (WK-23, snippets/wk-matching-products.liquid).
  // QA fix round 2026-08-21: the doc calls these the "belt buckle picture upsell" and "belt
  // buckle message upsell" — separate markup from <product-block> (a plain <a> per item), so
  // the listener above never matched. Same buildItem() shape, reading the data-* attributes
  // added to wk-matching-products.liquid alongside this fix.
  document.addEventListener('click', function (evt) {
    const link = evt.target.closest('.wk-matching-products__link');
    if (!link) return;

    pushEcommerceEvent('select_item', {
      value: Number(link.dataset.productPrice) || 0,
      items: [
        buildItem({
          productId: link.dataset.productId,
          variantId: link.dataset.variantId,
          productName: link.dataset.productName,
          productType: link.dataset.productType,
          variantTitle: '',
          price: link.dataset.productPrice,
          quantity: 1,
          itemListName: link.dataset.itemListName || undefined,
        }),
      ],
    });
  });

  // remove_from_cart: fires on the "x" (full removal) and "-" (decrease) controls.
  // Independent listener from the theme's own CartForm delegate in main.js — click events
  // still bubble to document after evt.preventDefault(), so this doesn't need to touch main.js.
  document.addEventListener('click', function (evt) {
    const removeBtn = evt.target.closest('.cart-item__remove');
    const decreaseBtn = evt.target.closest('.quantity-down');
    const control = removeBtn || decreaseBtn;
    if (!control) return;

    const cartItem = control.closest('.cart-item');
    if (!cartItem || !cartItem.dataset.wkProductId) return;

    const removedQuantity = removeBtn ? Number(cartItem.dataset.wkQuantity) || 1 : 1;
    const unitPrice = Number(cartItem.dataset.wkUnitPrice) || 0;
    const personalizationType = cartItem.dataset.wkPersonalizationType || '';
    const personalizationValue = Number(cartItem.dataset.wkPersonalizationValue) || 0;

    pushEcommerceEvent('remove_from_cart', {
      value: unitPrice * removedQuantity,
      items: [
        buildItem({
          productId: cartItem.dataset.wkProductId,
          variantId: cartItem.dataset.wkVariantId,
          productName: cartItem.dataset.wkProductName,
          productType: cartItem.dataset.wkProductType,
          variantTitle: cartItem.dataset.wkVariantTitle,
          price: unitPrice,
          quantity: removedQuantity,
          personalizationType: personalizationType,
          personalizationValue: personalizationValue,
        }),
      ],
      personalization: { type: personalizationType, value: personalizationValue },
    });
  });

  // social_cta: delegated across every social-media.liquid instance (header, footer, etc.)
  document.addEventListener('click', function (evt) {
    const link = evt.target.closest('.social__link');
    if (!link) return;
    const label = link.querySelector('.visually-hidden');
    const platform = (label ? label.textContent : link.getAttribute('title') || '').trim().toLowerCase();
    if (!platform) return;
    push({ event: 'social_cta', social: { platform: platform } });
  });

  // navigation_cta: header/mega-menu tiers + footer link lists.
  document.addEventListener('click', function (evt) {
    const navLink = evt.target.closest('.navigation__link');
    const footerLink = evt.target.closest('.section-footer__menu-block__menu a, .lower-menu a');
    const link = navLink || footerLink;
    if (!link) return;

    let navType = 'top';
    if (footerLink) {
      navType = 'footer';
    } else if (navLink.closest('.navigation__tier-3')) {
      navType = 'mega';
    } else if (navLink.closest('.navigation__tier-2')) {
      navType = 'sub';
    }

    push({
      event: 'navigation_cta',
      navigation: { type: navType, text: link.textContent.trim() },
    });
  });

  // article_cta: Press Coverage page (/pages/press-coverage) only. The "View article" button
  // lives inside Symmetry's generic text-column block (brand + date + button), which is reused
  // elsewhere on the site for unrelated content — scoped by pathname so this doesn't misfire on
  // other pages built with the same component.
  // Known gap (flagged to Frank 2026-08-08): the page's content only has Brand (h3) and Date
  // (paragraph) per article — no headline/title field exists anywhere in the CMS content for
  // this section. article.title is left blank rather than duplicating brand into it; reads a
  // future data-article-title attribute first in case per-article headline text gets added later.
  document.addEventListener('click', function (evt) {
    if (!window.location.pathname.includes('/pages/press-coverage')) return;
    const link = evt.target.closest('.text-column__button');
    if (!link) return;
    const column = link.closest('.text-column');
    if (!column) return;

    const brand = (column.querySelector('.text-column__title')?.textContent || '').trim();
    const date = (column.querySelector('.text-column__text')?.textContent || '').trim();
    const title = (column.dataset.articleTitle || '').trim();

    push({
      event: 'article_cta',
      article: { brand: brand, title: title, date: date },
    });
  });

  // sign_up / login: Shopify's create_customer / customer_login forms redirect on success
  // (no inline "success" state to hook), so mark intent on submit and confirm on the next
  // page load (main-account.liquid). Guarded by a TTL so a failed-login reload of the
  // login page doesn't leave a stale flag to misfire on some unrelated later account visit.
  // QA fix round 2026-08-20: widened from 30s to 3min — code-verified the submit/confirm wiring
  // is otherwise correct (form IDs match, flag-set confirmed live), so a real login/signup
  // round-trip (typing credentials, page load) plausibly exceeded the old 30s window.
  document.addEventListener('submit', function (evt) {
    const form = evt.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id === 'customer_login') {
      sessionStorage.setItem('wk_pending_login', String(Date.now()));
    } else if (form.id === 'create_customer') {
      sessionStorage.setItem('wk_pending_signup', String(Date.now()));
    }
  });

  // add_to_cart: ONE delegated document-level listener covers both the PDP's own form and
  // Quick Buy (collection-grid instant add-to-cart).
  // QA fix round 2026-08-21: previously bound per-instance inside wk-datalayer-product.liquid's
  // own <script>, which worked for the PDP (script runs at real page load) but silently never
  // ran for Quick Buy — live-verified that Quick Buy fetches this same product template
  // fragment and injects it via a method that does NOT execute embedded <script> tags (the old
  // mount() code showed up as inert text in the DOM). Confirmed live that 'on:cart:add' itself
  // still bubbles to document regardless of how the form was injected, and that the JSON data
  // blob in wk-datalayer-product.liquid survives injection too (Quick Buy's own id-dedup step
  // appends "QuickBuy" to its id to avoid colliding with the same product's blob elsewhere on
  // the page — hence the prefix match below instead of an exact id lookup).
  document.addEventListener('on:cart:add', function (evt) {
    if (!(evt.target instanceof Element)) return;
    const productForm = evt.target.closest('product-form') || evt.target;
    const variantId = evt.detail && evt.detail.variantId;
    if (!variantId) return;

    const scope = productForm.closest('.quickbuy-container') || document;
    const blobEl = scope.querySelector('script[id^="wk-datalayer-variants-"]');
    if (!blobEl) return;

    let data;
    try {
      data = JSON.parse(blobEl.textContent);
    } catch (e) {
      return;
    }
    const variant = (data.variants || []).filter(function (v) { return v.id === variantId; })[0];
    if (!variant) return;

    const qtyInput = productForm.querySelector('[name="quantity"]');
    const quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    const engraveCheckbox = productForm.querySelector('.wk-engrave__checkbox');
    const isEngraved = !!(engraveCheckbox && engraveCheckbox.checked);
    const unitPrice = variant.price / 100;
    const engravePrice = Number(data.engravePrice) || 0;

    const item = buildItem({
      productId: data.productId,
      variantId: variantId,
      productName: data.productName,
      productType: data.productType,
      variantTitle: variant.title === 'Default Title' ? '' : variant.title,
      price: unitPrice,
      quantity: quantity,
      personalizationType: isEngraved ? 'buckle_engraving' : '',
      personalizationValue: isEngraved ? engravePrice : 0,
    });

    pushEcommerceEvent('add_to_cart', {
      value: (unitPrice * quantity) + (isEngraved ? engravePrice : 0),
      items: [item],
      personalization: { type: isEngraved ? 'buckle_engraving' : '', value: isEngraved ? engravePrice : 0 },
    });
  });

  // site_search (header predictive-search dropdown surface).
  // QA fix round 2026-08-20, corrected: the FIRST attempt watched for #predictive-search-results
  // (the id sections/predictive-search.liquid renders) via MutationObserver, on the theory that
  // fragment gets injected verbatim. Live-verified via Playwright that it does NOT — the actual
  // header search JS fetches that section, then extracts/reassembles just the inner product grid
  // into its OWN container (id="HeaderSearchResults", present in the static header markup from
  // page load, never "added" as a node), discarding any wrapper div/data-attribute the section
  // rendered. So watching for a specific id add never matched. Fixed: watch #HeaderSearchResults
  // for its children changing (real content arriving), then read the term directly off the live
  // search input's value at that moment — sidesteps the fragment-reassembly problem entirely.
  // Dedup on lastTerm still prevents any repeat/multi-mutation-record firing for one query.
  (function () {
    let lastTerm = null;
    function checkForResults() {
      const resultsEl = document.getElementById('HeaderSearchResults');
      if (!resultsEl || resultsEl.children.length === 0) return;
      const input = document.querySelector('input[name="q"]');
      const term = input ? input.value.trim() : '';
      if (term && term !== lastTerm) {
        lastTerm = term;
        push({ event: 'site_search', search: { term: term } });
      }
    }
    function startObserving() {
      const observer = new MutationObserver(checkForResults);
      observer.observe(document.body, { childList: true, subtree: true });
    }
    if (document.body) {
      startObserving();
    } else {
      document.addEventListener('DOMContentLoaded', startObserving);
    }
  })();

  // view_cart (cart-drawer surface).
  // QA fix round 2026-08-20, corrected twice: (1) original push lived inline in
  // sections/main-cart.liquid, which only renders for the dedicated /cart PAGE — this store's
  // real cart UI is the DRAWER (sections/cart-drawer.liquid), whose <cart-form> refreshes via a
  // DOM-merge (refreshFromHtml in cart-drawer.js), not innerHTML replacement, so an inline script
  // there never reaches the DOM on refresh. (2) Second attempt listened for the DOCUMENT-level
  // 'dispatch:cart-drawer:open' custom event cart-drawer.js itself listens for — live-verified
  // via Playwright that manually dispatching that event correctly triggers the fetch/push logic
  // below, but the REAL add-to-cart flow never actually dispatches it (some other code path opens
  // the drawer directly, e.g. calling the SideDrawer instance's own .open() method rather than
  // going through the document event). Fixed: watch the <cart-drawer> element's own `aria-hidden`
  // attribute instead — that's the one thing guaranteed true regardless of which code path opened
  // it, since aria-hidden="false" is what actually makes the drawer visible/openable to begin with.
  (function () {
    function fetchAndPushViewCart() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          if (!cart || !cart.items || cart.items.length === 0) return;
          const items = cart.items.map(function (item) {
            let personalizationType = '';
            let personalizationValue = 0;
            const engravingProp = (item.properties || {})['Personalized Buckle Engraving'];
            if (engravingProp) {
              personalizationType = 'buckle_engraving';
              personalizationValue = 50;
            } else if (item.handle === 'buckle-engraving') {
              personalizationType = 'buckle_engraving';
              personalizationValue = item.final_price / 100;
            }
            return buildItem({
              productId: item.product_id,
              variantId: item.id,
              productName: item.product_title,
              productType: item.product_type,
              variantTitle: item.variant_title === 'Default Title' ? '' : item.variant_title,
              price: item.final_price / 100,
              quantity: item.quantity,
              personalizationType: personalizationType,
              personalizationValue: personalizationValue,
            });
          });
          pushEcommerceEvent('view_cart', {
            value: cart.total_price / 100,
            items: items,
          });
        })
        .catch(function () {});
    }

    function watchDrawer(drawer) {
      if (drawer.dataset.wkViewCartBound) return;
      drawer.dataset.wkViewCartBound = 'true';
      let wasHidden = drawer.getAttribute('aria-hidden') !== 'false';
      const observer = new MutationObserver(function () {
        const isHidden = drawer.getAttribute('aria-hidden') !== 'false';
        if (wasHidden && !isHidden) {
          fetchAndPushViewCart();
        }
        wasHidden = isHidden;
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    }

    function startObserving() {
      const existing = document.querySelector('cart-drawer');
      if (existing) watchDrawer(existing);
      const bodyObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (!(node instanceof Element)) return;
            const drawer = node.tagName === 'CART-DRAWER' ? node : node.querySelector && node.querySelector('cart-drawer');
            if (drawer) watchDrawer(drawer);
          });
        });
      });
      bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    if (document.body) {
      startObserving();
    } else {
      document.addEventListener('DOMContentLoaded', startObserving);
    }
  })();

  return { push, buildItem, pushEcommerceEvent };
})();
